import React from 'react';
import { ChevronLeft, Shield, Star, MapPin, Briefcase, Clock, Calendar, Stethoscope, Award } from 'lucide-react';
import { useApp } from '../context/AppContext';

const ProfileView = () => {
  const { selectedProvider, setView } = useApp();
  const p = selectedProvider;
  
  if (!p) return null;
  
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const availableDays = p.availableDays || p.availability || [];
  const services = p.services || [];
  const certifications = p.certifications || [];
  const insuranceAccepted = p.insuranceAccepted || [];
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => setView('search')} className="flex items-center gap-2 text-slate-600 hover:text-teal-600 mb-6">
          <ChevronLeft className="w-5 h-5" />Back to Search
        </button>
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 p-8">
            <div className="flex flex-col md:flex-row gap-6">
              <img 
                src={p.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || p.firstName + ' ' + p.lastName)}&background=ffffff&color=0d9488&size=300`} 
                alt={p.name} 
                className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-lg" 
                onError={e => e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || 'Provider')}&background=ffffff&color=0d9488`}
              />
              <div className="flex-1 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold">{p.name || `${p.firstName} ${p.lastName}`}</h1>
                  {p.isVerified && <span className="flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-sm"><Shield className="w-4 h-4" />Verified</span>}
                </div>
                <p className="text-teal-100 text-lg mb-1">{p.providerType?.name || p.providerType}</p>
                <p className="text-teal-200 mb-4">{p.specialty}</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-300 fill-amber-300" />{p.rating || p.averageRating || 0} ({p.totalReviews || 0} reviews)</span>
                  <span className="flex items-center gap-2 text-teal-100"><MapPin className="w-5 h-5" />{p.location?.city || p.city}, {p.location?.state || p.state}</span>
                  <span className="flex items-center gap-2 text-teal-100"><Briefcase className="w-5 h-5" />{p.yearsOfExperience} years</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm">Starting at</p>
                <p className="text-4xl font-bold text-white">${p.hourlyRate}</p>
                <p className="text-teal-200">/hour</p>
              </div>
            </div>
          </div>
          <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="text-xl font-semibold text-slate-800 mb-4">About</h2>
                <p className="text-slate-600">{p.bio}</p>
              </section>
              {services.length > 0 && (
                <section>
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">Services</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {services.map((s,i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <Stethoscope className="w-5 h-5 text-teal-600" />
                        <span className="text-slate-700">{typeof s === 'string' ? s : s.name}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {certifications.length > 0 && (
                <section>
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">Certifications</h2>
                  <div className="flex flex-wrap gap-3">
                    {certifications.map((c,i) => (
                      <span key={i} className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                        <Award className="w-5 h-5 text-amber-600" />{typeof c === 'string' ? c : (c.abbreviation || c.name)}
                      </span>
                    ))}
                  </div>
                </section>
              )}
              {insuranceAccepted.length > 0 && (
                <section>
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">Insurance Accepted</h2>
                  <div className="flex flex-wrap gap-3">
                    {insuranceAccepted.map((ins,i) => (
                      <span key={i} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium">{typeof ins === 'string' ? ins : ins.name}</span>
                    ))}
                  </div>
                </section>
              )}
            </div>
            <div>
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-6 border border-teal-100 sticky top-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Book an Appointment</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-slate-600"><Clock className="w-5 h-5 text-teal-600" />Usually responds within 2 hours</div>
                  <div className="flex items-center gap-3 text-slate-600"><Calendar className="w-5 h-5 text-teal-600" />Available: {days.filter((_,i) => availableDays.includes(i)).join(', ') || 'Contact for availability'}</div>
                </div>
                <button onClick={() => setView('booking')} className="w-full py-4 bg-gradient-to-r from-teal-600 to-emerald-500 text-white rounded-xl font-semibold shadow-lg shadow-teal-200">Book Now</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
